import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MiscService } from '../../../services/misc/misc.service';
import { TooltipDirective } from '../../../common/directives/tooltip/tooltip.directive';
import { TruncatePipe } from '../../../pipes/truncate/truncate';
import { ShopifyAffiliateAssignmentDrawerContext } from './shopify-affiliate-assignment.types';

@Component({
  selector: 'app-shopify-affiliate-assignment',
  standalone: true,
  imports: [CommonModule, TooltipDirective, TruncatePipe],
  templateUrl: './shopify-affiliate-assignment.component.html',
  styleUrl: './shopify-affiliate-assignment.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShopifyAffiliateAssignmentComponent implements OnInit, OnDestroy {
  @ViewChild('drawerDialog', { static: false }) drawerDialog?: ElementRef<HTMLDialogElement>;
  @ViewChild('openDrawerButton', { static: false }) openDrawerButton?: ElementRef<HTMLButtonElement>;

  readonly miscService = inject(MiscService);
  readonly assignment = signal<ShopifyAffiliateAssignmentDrawerContext | null>(null);

  countdownNowMs = Date.now();
  private countdownIntervalId: ReturnType<typeof setInterval> | undefined;

  ngOnInit(): void {
    this.countdownIntervalId = setInterval(() => {
      this.countdownNowMs = Date.now();
    }, 1000);
  }

  open(context: ShopifyAffiliateAssignmentDrawerContext): void {
    this.assignment.set(context);
    // el-dialog watches panel visibility and can auto-close if showModal runs before layout.
    // Prefer Invoker Commands (`command="show-modal"`) after paint, matching other drawers.
    this.scheduleDrawerOpen();
  }

  private scheduleDrawerOpen(): void {
    const tryOpen = (): void => {
      const btn = this.openDrawerButton?.nativeElement;
      if (btn) {
        btn.click();
        return;
      }

      const dialog = this.drawerDialog?.nativeElement;
      const host = dialog?.closest('el-dialog') as (HTMLElement & { show?: () => void }) | null;
      if (host && typeof host.show === 'function') {
        host.show();
        return;
      }

      if (dialog && !dialog.open) {
        dialog.showModal();
      }
    };

    requestAnimationFrame(() => requestAnimationFrame(tryOpen));
  }

  closePanel(): void {
    this.drawerDialog?.nativeElement?.close();
  }

  displayCommission(assignment: ShopifyAffiliateAssignmentDrawerContext): string {
    const v = assignment.commissionPercent;
    if (v === undefined || v === null || v === '') {
      return '—';
    }
    const n = typeof v === 'string' ? parseFloat(v) : v;
    return Number.isFinite(n) ? `${n}%` : '—';
  }

  currencyForDisplay(assignment: ShopifyAffiliateAssignmentDrawerContext): string {
    return (assignment.currencyCode || 'USD').toUpperCase();
  }

  isAssignmentExpired(assignment: ShopifyAffiliateAssignmentDrawerContext): boolean {
    if (!assignment.expiresAt) {
      return false;
    }

    const expiresAtMs = new Date(assignment.expiresAt).getTime();
    if (!Number.isFinite(expiresAtMs)) {
      return false;
    }

    return expiresAtMs <= this.countdownNowMs;
  }

  formatTimeLeftLabel(expiresAt: string | Date | null | undefined): string {
    const raw = this.miscService.formatTimeLeft(expiresAt, this.countdownNowMs);
    if (raw === 'Expired' || raw === 'N/A') {
      return raw;
    }
    const dayHourMin = /^(\d+d)\s+(\d+)h\s+\d+m$/;
    const m = raw.match(dayHourMin);
    if (m) {
      return `${m[1]} ${m[2]}h left`;
    }
    return `${raw} left`;
  }

  copyItem(value: string | null | undefined, label: string): void {
    if (!value) {
      return;
    }
    this.miscService.copyToClipboard(value, label);
  }

  ngOnDestroy(): void {
    if (this.countdownIntervalId) {
      clearInterval(this.countdownIntervalId);
    }
  }
}
